import { useState, useEffect } from 'react'
import { DateTime } from 'luxon'
import AuthService from 'services/auth'
import { isCanceled, logApiError } from 'services/apiError'
import useAbortController from 'hooks/useAbortController'
import TimeWindow from 'services/timeWindow'
import DreData from 'services/DreData'
import Select from 'components/Inputs/Select'
import ItemTabs from 'components/ItemTabs'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import DreTable from './Table'
import DreChart from './Chart'
import DreExpenses from './Expenses'

import {
  Container,
  FilterContainer,
  LoadingContainer,
  InfoText,
  WarningText
} from './styles'

const VIEW_TABS = [
  { label: 'Tabela', value: 'table' },
  { label: 'Gráfico', value: 'chart' },
  { label: 'Despesas', value: 'expenses' }
]

export default function Dre() {
  const now = DateTime.now().setLocale('pt-BR')
  const businessId = AuthService.getBusinessId()
  const requestAuth = AuthService.getRequestAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [year, setYear] = useState({ label: now.toFormat('yyyy'), value: now.year })
  const [statement, setStatement] = useState([])
  const [costs, setCosts] = useState(null)
  const [expenses, setExpenses] = useState([])
  const [view, setView] = useState('table')
  const { abortarPendentes } = useAbortController()

  useEffect(() => {
    getData()
  }, [year])

  /*
    O ano inteiro vem numa requisição só (`GET /businesses/:id/dre`). Em StrictMode o efeito
    roda duas vezes na montagem, então `abortarPendentes` cancela a anterior antes de disparar
    a nova — sem isso, a resposta mais lenta sobrescreve a tela que a outra já montou.
  */
  const getData = async () => {
    const signal = abortarPendentes()

    try {
      setLoading(true)
      setError(false)

      const result = await DreData.load({ businessId, year: year.value, requestAuth, signal })

      setStatement(result.statement)
      setCosts(result.costs)
      setExpenses(result.expenses)
    } catch (apiError) {
      if (isCanceled(apiError)) return
      logApiError(apiError)
      setError(true)
    } finally {
      if (!signal.aborted) setLoading(false)
    }
  }

  return (
    <Container>
      <FilterContainer>
        <Select
          name='year'
          placeholder='Ano...'
          value={year}
          options={TimeWindow.getYears('2021', now.year.toString())}
          onChange={(_, option) => setYear(option)}
        />
      </FilterContainer>
      <ItemTabs
        tabs={VIEW_TABS}
        selectedTab={view}
        tabWidth={120}
        onTabChange={setView}
        containerStyle={{padding: 0}}
      />
      {loading ? (
        <LoadingContainer>
          <Loading/>
          <InfoText>Gerando DRE...</InfoText>
        </LoadingContainer>
      ) : error ? (
        <LoadingContainer>
          <WarningText>Não foi possível carregar a DRE. Tente novamente.</WarningText>
        </LoadingContainer>
      ) : view === 'chart' ? (
        <DreChart statement={statement} year={year.value}/>
      ) : view === 'expenses' ? (
        <DreExpenses year={year.value} costs={costs} expenses={expenses} onChange={getData}/>
      ) : (
        <DreTable statement={statement}/>
      )}
    </Container>
  )
}
